import React, { useEffect, useId, useRef, useState } from 'react';
import { Check, Edit3, Loader2, X } from 'lucide-react';

interface EditableFilenameProps {
  value: string;
  onSave: (value: string) => Promise<void> | void;
  isLoading?: boolean;
  maxLength?: number;
  textClassName?: string;
  inputClassName?: string;
}

const EditableFilename: React.FC<EditableFilenameProps> = ({
  value,
  onSave,
  isLoading = false,
  maxLength = 255,
  textClassName,
  inputClassName
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [hasError, setHasError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [inputWidth, setInputWidth] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textMeasureRef = useRef<HTMLSpanElement>(null);
  const inputId = useId();
  const saving = isSaving || isLoading;
  const displayTextClassName = textClassName ?? 'text-sm font-medium text-slate-900 dark:text-white';
  const inputTextClassName = inputClassName ?? 'text-sm font-medium text-slate-900 dark:text-zinc-100';
  const measureTextClassName = textClassName ?? inputTextClassName;

  useEffect(() => {
    if (!isEditing) {
      setDraft(value);
      setHasError(false);
    }
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing || !textMeasureRef.current) return;
    const measuredWidth = textMeasureRef.current.offsetWidth;
    const paddingWidth = 24;
    setInputWidth(measuredWidth + paddingWidth);
  }, [draft, isEditing]);

  const handleCancel = () => {
    setDraft(value);
    setHasError(false);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const nextValue = draft.trim();
    if (!nextValue) {
      setHasError(true);
      return;
    }
    if (nextValue === value) {
      setHasError(false);
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setHasError(false);
    try {
      await onSave(nextValue);
      setIsEditing(false);
    } catch {
      setHasError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!saving) {
        void handleSave();
      }
    }
  };

  if (isEditing) {
    return (
      <div className="relative flex items-center gap-2 min-w-0" onClick={(event) => event.stopPropagation()}>
        <label className="sr-only" htmlFor={inputId}>
          Edit filename
        </label>
        <span
          ref={textMeasureRef}
          className={`absolute invisible whitespace-pre ${measureTextClassName}`}
          aria-hidden="true"
        >
          {draft || ' '}
        </span>
        <input
          id={inputId}
          ref={inputRef}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            if (hasError) {
              setHasError(false);
            }
          }}
          onKeyDown={handleKeyDown}
          maxLength={maxLength}
          disabled={saving}
          className={`h-8 min-w-0 rounded-md border bg-white px-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:bg-zinc-900 ${inputTextClassName} ${
            hasError
              ? 'border-red-400 focus:ring-red-200'
              : 'border-slate-200 focus:border-indigo-400 dark:border-zinc-700'
          }`}
          style={{
            width: inputWidth ? `${inputWidth}px` : undefined,
            minWidth: '6ch',
            maxWidth: '100%'
          }}
        />
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            void handleSave();
          }}
          disabled={saving}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
          title="Save filename"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handleCancel();
          }}
          disabled={saving}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
          title="Cancel edit"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="group flex min-w-0 items-center gap-2 text-left">
      <span className={`truncate ${displayTextClassName}`}>
        {value}
      </span>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setIsEditing(true);
        }}
        className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 group-hover:opacity-100 dark:text-zinc-500 dark:hover:bg-zinc-800"
        title="Rename file"
      >
        <Edit3 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default EditableFilename;
